import React, { useState } from 'react';
import { supabase } from '../../utils/supabase';

const supabaseStorageUrl = "https://xsuruuynxrxrarvalzjb.supabase.co/storage/v1/object/public/images/";

const ImageModal = ({ isOpen, onClose, product, onImageUpdate }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen válido');
        return;
      }
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen debe ser menor a 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Por favor selecciona una imagen');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generar nombre único para el archivo
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${product.id}_${Date.now()}.${fileExt}`;

      // Subir archivo a Supabase Storage
      const { data, error } = await supabase.storage
        .from('images')
        .upload(fileName, selectedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error('Error al subir la imagen: ' + error.message);
      }

      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(fileName);

      // Actualizar el producto en la base de datos
      const { error: updateError } = await supabase
        .from('products')
        .update({ image: fileName })
        .eq('id', product.id);

      if (updateError) {
        throw new Error('Error al actualizar el producto: ' + updateError.message);
      }

      // Notificar al componente padre
      if (onImageUpdate) {
        onImageUpdate(product.id, fileName);
      }

      setUploadProgress(100);
      setTimeout(() => {
        onClose();
        setSelectedFile(null);
        setIsUploading(false);
        setUploadProgress(0);
      }, 1000);

    } catch (error) {
      console.error('Error al subir imagen:', error);
      alert('Error al subir la imagen: ' + error.message);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getImageUrl = () => {
    if (product.image) {
      return supabaseStorageUrl + product.image;
    }
    return supabaseStorageUrl + 'blank.jpg';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Gestionar Imagen - {product.name}
                </h3>
                
                {/* Imagen actual */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Imagen Actual:</h4>
                  <div className="flex justify-center">
                    <img
                      src={getImageUrl()}
                      alt={product.name}
                      className="w-48 h-48 object-contain border border-gray-300 rounded-lg"
                      onError={(e) => {
                        e.target.src = supabaseStorageUrl + 'blank.jpg';
                      }}
                    />
                  </div>
                </div>

                {/* Subir nueva imagen */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Subir Nueva Imagen:</h4>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    disabled={isUploading}
                  />
                </div>

                {/* Vista previa de la nueva imagen */}
                {selectedFile && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Vista Previa:</h4>
                    <div className="flex justify-center">
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Vista previa"
                        className="w-48 h-48 object-contain border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Barra de progreso */}
                {isUploading && (
                  <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Subiendo imagen... {uploadProgress}%</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Subiendo...' : 'Subir Imagen'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal; 